import { MultiCollapsible } from "@bonliva-crm/ui";
import { FormatOptionLabelMeta } from "react-select5";
import {
  ProfessionFilterModalInnerProps,
  getProfessionToValue,
  renderSelectType,
  Option
} from "../../utils";
import { ComponentProps, useMemo, useState } from "react";
import { useGlobalStore } from "@hooks";
import locale from "@bonliva-crm/locale";
import { SelectType } from "@bonliva-crm/shared";
import { ExperienceAndJobofferFilter } from "./common/experience-and-joboffer-filter";
import {
  baseOnChange,
  commonCollapsibleProps,
  isCollapsed,
  isGroupSelected,
  filterOption,
  isOptionStandAlone,
  onCollapse,
  parseMethod,
  parseSpec
} from "./utils/psykolog-utils";
import { groupBy, sortBy } from "lodash";
import { isFilterSelected } from "../../../../utils/is-filter-selected";

export const PsykologFilter = (props: ProfessionFilterModalInnerProps) => {
  // const classes = useStyles();
  const store = useGlobalStore();
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
  const professions = props?.value?.professions;
  // const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  //
  // const handleChange = (event: any, value: Option[]) => {
  //   setSelectedOptions(value);
  // };

  const professionObject = useMemo(() => {
    return props?.value?.[getProfessionToValue(professions?.[0])];
  }, [props?.value, professions]);

  if (!("experience" in professionObject) || !("methods" in professionObject)) {
    return null;
  }

  const valueExp = professionObject.experience.value;
  const typeExp = professionObject.experience.type;
  const valueMethods = professionObject.methods.value;
  const typeMethods = professionObject.methods.type;

  const areaOfExpertise = store.cache.getAreaOfExpOnProfessions(
    props?.value?.professions
  );

  const options: Option[] = [];

  areaOfExpertise.forEach((aoe) => {
    const specializationsToOptions = aoe.Specializations.map(parseSpec);

    specializationsToOptions.forEach((spec) => {
      if (!options.some((x) => spec.value.startsWith(x.value))) {
        options.push({
          label: spec.value.split("|||")[0],
          value: spec.value.split("|||")[0]
        });
      }
      options.push({
        label: spec.label,
        value: spec.value
      });
    });
  });

  const onChange = baseOnChange(options, (finalValues) => {
    props.onChange({
      ...props?.value,
      [getProfessionToValue(props?.value?.professions?.[0])]: {
        experience: {
          value: finalValues.map((x) => x.value),
          type: typeExp
        },
        methods: {
          value: valueMethods,
          type: typeMethods
        }
      },
      experience: !!finalValues?.length,
      interested: finalValues.length > 0 ? props?.value?.interested : "null",
      experienced: finalValues.length > 0 ? props?.value?.experienced : "null"
    });
  });

  const multiCollapsibleExpProps: ComponentProps<typeof MultiCollapsible<Option>> = {
    ...commonCollapsibleProps,
    formatOptionLabel: (option: Option, meta: FormatOptionLabelMeta<Option>) => {
      if (meta.context === "value") {
        return option.label.split("|||")[1] ?? option.label;
      }
      return option.label;
    },
    placeholder: null,
    inputLabel: locale.Specialization,
    options,
    value: valueExp.toLabelValue(),
    onChange,
    isOptionSelected: isGroupSelected(valueExp, options, true),
    isIndeterminate: isGroupSelected(valueExp, options, false),
    filterOption: filterOption(collapsedGroups),
    isCollapsed: isCollapsed(collapsedGroups),
    isOptionStandAlone: isOptionStandAlone(options),
    onCollapse: onCollapse(collapsedGroups, setCollapsedGroups),
    showBadge: false,
    isClearable: isFilterSelected(props?.value?.psykolog.experience),
    onClear: () => {
      props.onChange({
        ...props?.value,
        psykolog: {
          ...props?.value?.psykolog,
          experience: {
            type: SelectType.ANY,
            value: []
          }
        },
        experienced: null,
        interested: null
      });
    },
    menuChildren: {
      prependedChildren: renderSelectType(typeExp, (t: SelectType) => {
        props.onChange({
          ...props?.value,
          [getProfessionToValue(props?.value?.professions?.[0])]: {
            experience: {
              value: valueExp,
              type: t
            },
            methods: {
              value: valueMethods,
              type: typeMethods
            }
          }
        });
      })
    }
  };

  const methods = store.cache.getMethodsOnProfession(professions?.[0]);
  const methodOptions: Option[] = [];

  const methodGroupedOptions = groupBy(methods.map(parseMethod), "parent");

  for (const group in methodGroupedOptions) {
    const children = methodGroupedOptions[group].map((x) => ({
      label: x.label,
      value: x.value,
      parent: group
    }));

    methodOptions.push({
      label: group,
      value: group
    });

    methodOptions.push(...sortBy(children, "label"));
  }

  const onChangeMethod = baseOnChange(methodOptions, (finalValues) => {
    props.onChange({
      ...props?.value,
      [getProfessionToValue(props?.value?.professions?.[0])]: {
        experience: {
          value: valueExp,
          type: typeExp
        },
        methods: {
          value: finalValues.map((x) => x.value),
          type: typeMethods
        }
      },
      experience: !!finalValues?.length,
      interested: finalValues.length > 0 ? props?.value?.interested : "null",
      experienced: finalValues.length > 0 ? props?.value?.experienced : "null"
    });
  });

  const multiCollapsibleMethodProps: ComponentProps<typeof MultiCollapsible<Option>> = {
    ...commonCollapsibleProps,
    inputLabel: locale.Methods,
    placeholder: null,
    options: methodOptions,
    value: valueMethods.toLabelValue(),
    onChange: onChangeMethod,
    showBadge: false,
    isClearable: isFilterSelected(props?.value?.psykolog.methods),
    onClear: () => {
      props.onChange({
        ...props?.value,
        psykolog: {
          ...props?.value?.psykolog,
          methods: {
            type: SelectType.ANY,
            value: []
          }
        }
      });
    },
    isOptionSelected: isGroupSelected(valueMethods, methodOptions, true),
    isIndeterminate: isGroupSelected(valueMethods, methodOptions, false),
    filterOption: filterOption(collapsedGroups),
    isCollapsed: isCollapsed(collapsedGroups),
    isOptionStandAlone: isOptionStandAlone(methodOptions),
    onCollapse: onCollapse(collapsedGroups, setCollapsedGroups),
    menuChildren: {
      prependedChildren: renderSelectType(typeMethods, (t: SelectType) => {
        props.onChange({
          ...props?.value,
          [getProfessionToValue(props?.value?.professions?.[0])]: {
            experience: {
              value: valueExp,
              type: typeExp
            },
            methods: {
              value: valueMethods,
              type: t
            }
          }
        });
      })
    }
  };

  return (
    <>
      <MultiCollapsible<Option> {...multiCollapsibleExpProps} />
      <MultiCollapsible<Option> {...multiCollapsibleMethodProps} />
      {valueExp.length > 0 && <ExperienceAndJobofferFilter {...props} />}
    </>
  );
};
